import React from 'react';
import { Patient } from '../types/patient';
import { Heart, Activity, Thermometer, Wind } from 'lucide-react';

interface VitalsCardProps {
  patient: Patient;
}

export default function VitalsCard({ patient }: VitalsCardProps) {
  const { cardiovascularAnalysis } = patient.healthMetrics;

  // Helper function to get vital sign status color
  const getVitalStatus = (value: number | undefined, normalRange: { min: number; max: number }) => {
    if (!value) return 'text-gray-400';
    if (value < normalRange.min || value > normalRange.max) return 'text-danger-600';
    return 'text-medical-600';
  };

  // Blood pressure status
  const getBPStatus = () => {
    const sys = cardiovascularAnalysis.systolic;
    const dia = cardiovascularAnalysis.diastolic;
    if (sys >= 130 || dia >= 80) return 'text-danger-600';
    if (sys >= 120 || dia >= 75) return 'text-warning-600';
    return 'text-medical-600';
  };

  const vitals = [
    {
      icon: Thermometer,
      label: 'Body Temperature',
      value: cardiovascularAnalysis.bodyTemperature?.toFixed(1) || '98.6',
      unit: '°F',
      normalRange: 'Normal: 97-99°F',
      status: getVitalStatus(cardiovascularAnalysis.bodyTemperature, { min: 97, max: 99 }),
    },
    {
      icon: Heart,
      label: 'Blood Pressure',
      value: `${cardiovascularAnalysis.systolic}/${cardiovascularAnalysis.diastolic}`,
      unit: 'mmHg',
      normalRange: 'Normal: <120/80',
      status: getBPStatus(),
    },
    {
      icon: Activity,
      label: 'Pulse',
      value: cardiovascularAnalysis.heartRate?.toString() || '--',
      unit: 'bpm',
      normalRange: 'Normal: 60-100',
      status: getVitalStatus(cardiovascularAnalysis.heartRate, { min: 60, max: 100 }),
    },
    {
      icon: Wind,
      label: 'Respiratory Rate',
      value: cardiovascularAnalysis.respiratoryRate?.toString() || '--',
      unit: 'br/min',
      normalRange: 'Normal: 12-20',
      status: getVitalStatus(cardiovascularAnalysis.respiratoryRate, { min: 12, max: 20 }),
    },
  ];

  return (
    <div className="card p-3">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-brand-dark-600">Latest Vitals</h3>
        <span className="text-xs text-brand-dark-400">Last visit: {new Date(patient.lastVisit).toLocaleDateString()}</span>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {vitals.map((vital, index) => {
          const Icon = vital.icon;
          return (
            <div key={index} className="flex items-start space-x-2 p-1.5 rounded-lg">
              <div className="flex-shrink-0 p-1.5 bg-brand-ivory-200 rounded-lg">
                <Icon className={`w-4 h-4 ${vital.status}`} />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-xs font-medium text-brand-dark-600">{vital.label}</h4>
                <div className="mt-0.5">
                  <span className={`text-base font-bold ${vital.status}`}>
                    {vital.value}
                  </span>
                  <span className="text-xs text-brand-dark-400 ml-1">{vital.unit}</span>
                </div>
                <p className="text-xs text-brand-dark-400 mt-0.5">{vital.normalRange}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-3 pt-2 border-t border-brand-taupe-200">
        <p className="text-xs text-brand-dark-400 text-center">
          Vitals recorded during most recent patient visit
        </p>
      </div>
    </div>
  );
}
